import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import addYears from 'date-fns/addYears';
import isSameDay from 'date-fns/isSameDay';
import isWithinInterval from 'date-fns/isWithinInterval';
import orderBy from 'lodash/orderBy';
import { CRMContact, CRMToday } from '../../../types';

// Birthday is today
export function isBirthdayToday(params: { dateOfBirth: string }): boolean {
  const date = new Date();
  const dateBirth = new Date(params.dateOfBirth);

  const isDaySame = date.getDate() === dateBirth.getDate();
  const isMonthSame = date.getMonth() === dateBirth.getMonth();

  if (isDaySame && isMonthSame) {
    return true;
  } else {
    return false;
  }
}

// Birthday has been in the past
export function isBirthdayRecent(params: { dateOfBirth: string }): boolean {
  const date = new Date();
  const dateBirth = new Date(params.dateOfBirth);
  // This is the contact's birthday in the current year
  const birthday = new Date(
    date.getFullYear(),
    dateBirth.getMonth(),
    dateBirth.getDate()
  );
  // Using only the current year for birthdays
  // causes issues around new years
  const birthdayLast = addYears(birthday, -1);
  const birthdayNext = addYears(birthday, 1);

  // Last 30 days
  const recent = (checkDate: Date) =>
    isWithinInterval(checkDate, {
      start: addDays(date, -30),
      end: date,
    });

  if (recent(birthday) || recent(birthdayLast) || recent(birthdayNext)) {
    return true;
  } else {
    return false;
  }
}

// Birthday will be upcoming
export function isBirthdayUpcoming(params: { dateOfBirth: string }): boolean {
  const date = new Date();
  const dateBirth = new Date(params.dateOfBirth);
  // This is the contact's birthday in the current year
  const birthday = new Date(
    date.getFullYear(),
    dateBirth.getMonth(),
    dateBirth.getDate()
  );
  // Using only the current year for birthdays
  // causes issues around new years
  const birthdayLast = addYears(birthday, -1);
  const birthdayNext = addYears(birthday, 1);

  // Next 30 days
  const upcoming = (checkDate: Date) =>
    isWithinInterval(checkDate, {
      start: date,
      end: addDays(date, 30),
    });

  if (upcoming(birthday) || upcoming(birthdayLast) || upcoming(birthdayNext)) {
    return true;
  } else {
    return false;
  }
}

function is65Today(params: { dateOfBirth: string }): boolean {
  const date = new Date();
  const dateBirth = new Date(params.dateOfBirth);
  // This is the day this contact will turn 65
  const birthday65th = new Date(
    addYears(dateBirth, 65).getFullYear(),
    dateBirth.getMonth(),
    dateBirth.getDate()
  );

  if (isSameDay(date, birthday65th)) {
    return true;
  } else {
    return false;
  }
}

// Example: is65InMonthsOrLess({ dateOfBirth: contact.DateOfBirth, months: 6 })
// The above example checks to see if a contact will be
// turning 65 in 6 months or less
function is65InMonthsOrLess(params: {
  dateOfBirth: string;
  months: number;
}): boolean {
  const date = new Date();
  const dateBirth = new Date(params.dateOfBirth);
  // This is the day this contact will turn 65
  const birthday65th = new Date(
    addYears(dateBirth, 65).getFullYear(),
    dateBirth.getMonth(),
    dateBirth.getDate()
  );

  if (
    isWithinInterval(birthday65th, {
      start: date,
      end: addMonths(date, params.months),
    })
  ) {
    return true;
  } else {
    return false;
  }
}

export const contactTags = {
  reviewMonth6: '6 Mo Review',
  reviewMonth12: '12 Mo Review',
  conversionFOR: 'Conversion (FOR)',
  conversionMOO: 'Conversion (MOO)',
  conversionCFG: 'Conversion (CFG)',
  chargeback: 'Chargeback',
  birthdayToday: 'Birthday (Today)',
  birthdayRecent: 'Birthday (Recent)',
  birthdayUpcoming: 'Birthday (Upcoming)',
  medicareToday: 'Med Sup (65 Today)',
  medicareIn1: 'Med Sup (65 < 1 Mo)',
  medicareIn2: 'Med Sup (65 < 2 Mo)',
  medicareIn3: 'Med Sup (65 < 3 Mo)',
  medicareIn6: 'Med Sup (65 < 6 Mo)',
};

export function formatContacts(params: {
  contacts: CRMContact[];
  data: CRMToday;
}): CRMContact[] {
  const formattedContacts: CRMContact[] = [];

  const contacts = orderBy(params.contacts, 'LastName');

  contacts.forEach((contact) => {
    // Parse Carriers
    if (contact.Carriers) {
      contact.carriers = contact.Carriers.split(',');
    } else {
      contact.carriers = [];
    }

    // Parse Tags
    if (contact.Tags) {
      contact.tags = contact.Tags.split(',');
    } else {
      contact.tags = [];
    }

    // Tags for Policy Reviews and Conversions will be added here
    contact.contactTags = [];

    // Medicare Review
    contact.Medicare = null;
    params.data.medicare.forEach((item) => {
      const { ContactId } = item;
      if (contact.ContactId === ContactId.toString()) {
        // Add Medicare for Review
        contact.Medicare = item;

        // Add Tags for Medicare
        if (is65Today({ dateOfBirth: item.DateOfBirth + 'T00:00' })) {
          // Today is the 65th birthday of this contact
          contact.contactTags?.push(contactTags.medicareToday);
        } else if (
          is65InMonthsOrLess({
            dateOfBirth: item.DateOfBirth + 'T00:00',
            months: 1,
          })
        ) {
          // This contact will be 65 in 1 month or less
          contact.contactTags?.push(contactTags.medicareIn1);
        } else if (
          is65InMonthsOrLess({
            dateOfBirth: item.DateOfBirth + 'T00:00',
            months: 2,
          })
        ) {
          // This contact will be 65 in 2 months or less
          contact.contactTags?.push(contactTags.medicareIn2);
        } else if (
          is65InMonthsOrLess({
            dateOfBirth: item.DateOfBirth + 'T00:00',
            months: 3,
          })
        ) {
          // This contact will be 65 in 3 months or less
          contact.contactTags?.push(contactTags.medicareIn3);
        } else {
          // This contact will be 65 in 6 months or less
          contact.contactTags?.push(contactTags.medicareIn6);
        }
      }
    });

    // Birthday Review
    contact.Birthday = null;
    params.data.birthdays.forEach((item) => {
      const { ContactId } = item;
      if (contact.ContactId === ContactId.toString()) {
        // Add Birthday for Review
        contact.Birthday = item;

        // Add Tags for Birthday
        if (isBirthdayToday({ dateOfBirth: item.DateOfBirth + 'T00:00' })) {
          // Birthday is today
          contact.contactTags?.push(contactTags.birthdayToday);
        } else if (
          isBirthdayRecent({ dateOfBirth: item.DateOfBirth + 'T00:00' })
        ) {
          // Birthday has been in the past
          contact.contactTags?.push(contactTags.birthdayRecent);
        } else if (
          isBirthdayUpcoming({ dateOfBirth: item.DateOfBirth + 'T00:00' })
        ) {
          // Birthday will be upcoming
          contact.contactTags?.push(contactTags.birthdayUpcoming);
        }
      }
    });

    // Policy Reviews
    contact.Reviews = [];
    params.data.policyReviews.forEach((item) => {
      const { ContactId, PolicyReview } = item;
      if (contact.ContactId === ContactId.toString()) {
        if (PolicyReview === '6mo') {
          // If this tag hasn't already been added
          if (!contact.contactTags?.includes(contactTags.reviewMonth6)) {
            // Add tag
            contact.contactTags?.push(contactTags.reviewMonth6);
          }
        } else if (PolicyReview === '12mo') {
          // If this tag hasn't already been added
          if (!contact.contactTags?.includes(contactTags.reviewMonth12)) {
            // Add tag
            contact.contactTags?.push(contactTags.reviewMonth12);
          }
        }

        // Add Review
        contact.Reviews?.push(item);
      }
    });

    // Conversions
    contact.Conversions = [];
    params.data.conversions.forEach((item) => {
      const { ContactId, PolicyReview } = item;
      if (contact.ContactId === ContactId.toString()) {
        if (PolicyReview === 'CFG SafeShield') {
          // If this tag hasn't already been added
          if (!contact.contactTags?.includes(contactTags.conversionCFG)) {
            // Add tag
            contact.contactTags?.push(contactTags.conversionCFG);
          }
          // If this carrier hasn't already been added
          if (!contact.carriers?.includes('CFG')) {
            // Add carrier
            contact.carriers?.push('CFG');
          }
        } else if (PolicyReview === 'Foresters Strong Foundation') {
          // If this tag hasn't already been added
          if (!contact.contactTags?.includes(contactTags.conversionFOR)) {
            // Add tag
            contact.contactTags?.push(contactTags.conversionFOR);
          }
          // If this carrier hasn't already been added
          if (!contact.carriers?.includes('FOR')) {
            // Add carrier
            contact.carriers?.push('FOR');
          }
        } else if (PolicyReview === 'Mutual of Omaha Term Life Express') {
          // If this tag hasn't already been added
          if (!contact.contactTags?.includes(contactTags.conversionMOO)) {
            // Add tag
            contact.contactTags?.push(contactTags.conversionMOO);
          }
          // If this carrier hasn't already been added
          if (!contact.carriers?.includes('MOO')) {
            // Add carrier
            contact.carriers?.push('MOO');
          }
        }

        // Add Conversion
        contact.Conversions?.push(item);
      }
    });

    // Mutual of Omaha Children's Whole Life
    contact.MoOChildrensWL = null;
    params.data.moo_children_wl.forEach((item) => {
      const { ContactId } = item;
      if (contact.ContactId === ContactId.toString()) {
        contact.MoOChildrensWL = item;
      }
    });

    // Foresters Children's Whole Life
    contact.ForestersChildrensWL = null;
    params.data.foresters_children_wl.forEach((item) => {
      const { ContactId } = item;
      if (contact.ContactId === ContactId.toString()) {
        contact.ForestersChildrensWL = item;
      }
    });

    // Chargebacks
    contact.Chargebacks = [];
    params.data.chargebacks.forEach((item) => {
      const { ContactId } = item;
      if (contact.ContactId === ContactId.toString()) {
        // If this tag hasn't already been added
        if (!contact.contactTags?.includes(contactTags.chargeback)) {
          // Add tag
          contact.contactTags?.push(contactTags.chargeback);
        }

        // Add Chargeback
        contact.Chargebacks?.push(item);
      }
    });

    formattedContacts.push(contact);
  });

  return formattedContacts;
}
